import classNames from "classnames";
import type { VFC, ReactNode, ReactElement, ComponentPropsWithoutRef } from "react";
import { Children, useMemo } from "react";

import { KibelaTooltip } from "../KibelaTooltip";

type ButtonItemProps = {
  active: boolean;
  label: ReactNode;
  onClick: () => void;
  tabItemClassName?: string;
  buttonClassName?: string;
  tooltip?: string;
};

const ButtonItem: VFC<ButtonItemProps> = (props) => {
  const { active, label, onClick, tabItemClassName, buttonClassName, tooltip } = props;

  const tooltipId = useMemo(() => {
    return Math.random().toString(32).substring(2);
  }, []);

  return (
    <li className={classNames(tabItemClassName)}>
      <button
        type="button"
        role="tab"
        aria-selected={active}
        className={classNames(buttonClassName, active && "is-tab-active")}
        onClick={onClick}
        data-tip={tooltip}
        data-for={tooltipId}
        data-tip-disable={!tooltip}
      >
        {label}
      </button>
      <KibelaTooltip id={tooltipId} place="bottom" effect="solid" multiline />
    </li>
  );
};

type PanelProps = {
  active: boolean;
  children: ReactNode;
};

const Panel: VFC<PanelProps> = (props) => {
  const { active, children } = props;
  return (
    <div role="tabpanel" hidden={!active}>
      {children}
    </div>
  );
};

type TabProp = {
  active: boolean;
  label: ReactNode;
  children?: ReactNode;
  onButtonClick?: () => void;
  tabItemClassName?: string;
  buttonClassName?: string;
  disabled?: boolean;
  tooltip?: string;
  hidden?: boolean;
};

export const Tab: VFC<TabProp> = () => {
  return <></>;
};

type TabChildren = ReactElement<TabProp> | Array<ReactElement<TabProp>>;

const buttonItems = (children: TabChildren) => {
  return Children.map(children, (child) => {
    const { active, label, onButtonClick, tabItemClassName, buttonClassName, disabled, tooltip, hidden } = child.props;
    const handleClick = () => {
      if (disabled) {
        return;
      }
      onButtonClick && onButtonClick();
    };

    if (hidden) {
      return null;
    }

    return (
      <ButtonItem
        active={active}
        label={label}
        onClick={handleClick}
        tabItemClassName={tabItemClassName}
        buttonClassName={buttonClassName}
        tooltip={tooltip}
      />
    );
  });
};

const panels = (children: TabChildren) => {
  return Children.map(children, (child) => {
    const { active } = child.props;
    return <Panel active={active}>{child.props.children}</Panel>;
  });
};

type HorizontalTabsProps = {
  children: TabChildren;
  tabClassName?: string;
  panelClassName?: string;
} & ComponentPropsWithoutRef<"div">;

export const HorizontalTabs: VFC<HorizontalTabsProps> = ({ children, tabClassName, panelClassName, ...props }) => {
  return (
    <div role="tablist" {...props}>
      <ul className={classNames("horizontalTab", tabClassName)}>{buttonItems(children)}</ul>
      <div className={panelClassName}>{panels(children)}</div>
    </div>
  );
};
